import { Trash2, Plus } from "lucide-react";
import { NumIn, SelectIn, TextIn } from "@/components/editor/fields";
import { updateEvent, deleteEvent } from "@/lib/playOps";
import { sortedEvents, phasesSorted, uid } from "@/lib/playEngine";
import { BALL_STYLES } from "@/lib/playStyles";

export function BallPanel({ play, edit, setTool }) {
  const opts = play.players.map((p) => [p.id, `${p.label}${p.nickname ? ` · ${p.nickname}` : ""}`]);
  return (
    <div className="space-y-4" data-testid="ball-panel">
      <SelectIn label="Who has the ball at the start" value={play.ball.carrier || ""} options={[["", "Nobody"], ...opts]} onChange={(v) => edit.set((p) => ({ ...p, ball: { ...p.ball, carrier: v || null } }))} testId="select-ball-carrier" />
      <div className="flex items-center justify-between"><p className="label mb-0">Ball actions</p>
        <button className="btn btn-ghost" onClick={() => setTool("ball")} data-testid="btn-add-ball-action"><Plus size={16} />Add on field</button></div>
      {!play.ball.events.length && <p className="text-sm text-slate-500">No ball actions. Use the Ball Action tool to add a snap, handoff, pitch or pass.</p>}
      {sortedEvents(play).map((e) => (
        <div key={e.id} className="rounded-xl border border-[#1F3358] p-3 space-y-2" data-testid={`ball-event-${e.id}`}>
          <div className="flex gap-2 items-end">
            <div className="flex-1"><SelectIn label="Action" value={e.type} options={Object.entries(BALL_STYLES).map(([k, s]) => [k, s.label])} onChange={(v) => edit.set((p) => updateEvent(p, e.id, { type: v }))} testId={`ball-event-type-${e.id}`} /></div>
            <button className="btn btn-danger px-3" onClick={() => edit.set((p) => deleteEvent(p, e.id))} aria-label="Delete ball action" data-testid={`ball-event-delete-${e.id}`}><Trash2 size={16} /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <SelectIn label="From" value={e.from_id} options={opts} onChange={(v) => edit.set((p) => updateEvent(p, e.id, { from_id: v }))} testId={`ball-event-from-${e.id}`} />
            <SelectIn label="To" value={e.to_id} options={opts} onChange={(v) => edit.set((p) => updateEvent(p, e.id, { to_id: v }))} testId={`ball-event-to-${e.id}`} />
            <NumIn edit={edit} label="Happens at" value={e.time} onValue={(p, v) => updateEvent(p, e.id, { time: v })} testId={`ball-event-time-${e.id}`} />
            <NumIn edit={edit} label="Ball travel" min={0.1} max={10} value={e.duration} onValue={(p, v) => updateEvent(p, e.id, { duration: v })} testId={`ball-event-duration-${e.id}`} />
          </div>
        </div>
      ))}
      <p className="text-xs text-slate-500">The ball follows its carrier, travels during the action, and changes hands when it arrives.</p>
    </div>
  );
}

const PRESETS = ["Before the snap", "Snap", "Handoff", "Pass", "Catch", "Finish"];

export function PhasesPanel({ play, edit }) {
  const phases = phasesSorted(play);
  const add = (name) => edit.set((p) => {
    const last = Math.max(0, ...p.phases.map((x) => x.time));
    return { ...p, phases: [...p.phases, { id: uid("ph"), name, time: p.phases.length ? Math.round((last + 1) * 10) / 10 : 0 }] };
  });
  const upd = (id, patch) => (p) => ({ ...p, phases: p.phases.map((x) => (x.id === id ? { ...x, ...patch } : x)) });
  return (
    <div className="space-y-4" data-testid="phases-panel">
      <p className="text-sm text-slate-400">Phases are named moments in the play. Walk-through mode steps through them one at a time.</p>
      {phases.map((ph, i) => (
        <div key={ph.id} className="flex gap-2 items-end" data-testid={`phase-row-${i}`}>
          <div className="flex-1"><TextIn edit={edit} label={`Phase ${i + 1}`} value={ph.name} maxLength={40} onValue={(p, v) => upd(ph.id, { name: v || "Phase" })(p)} testId={`phase-name-${i}`} /></div>
          <div className="w-28"><NumIn edit={edit} label="At" value={ph.time} onValue={(p, v) => upd(ph.id, { time: v })(p)} testId={`phase-time-${i}`} /></div>
          <button className="btn btn-danger px-3" onClick={() => edit.set((p) => ({ ...p, phases: p.phases.filter((x) => x.id !== ph.id) }))} aria-label="Delete phase" data-testid={`phase-delete-${i}`}><Trash2 size={16} /></button>
        </div>
      ))}
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((n) => <button key={n} className="chip" disabled={play.phases.length >= 12} onClick={() => add(n)} data-testid={`add-phase-${n.replace(/\s+/g, "-").toLowerCase()}`}><Plus size={14} />{n}</button>)}
      </div>
    </div>
  );
}
